import React, { useEffect, useState } from 'react';
import { MultiSelect } from 'primereact/multiselect';
import { ColumnGroup } from 'primereact/columngroup';
import { DataTable } from 'primereact/datatable';
import { Tooltip } from 'primereact/tooltip';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { Row } from 'primereact/row';
import { GERMINATION_DISPLAY_NAMES, STEEPING_DISPLAY_NAMES, MEASURED_MOISTURES, MEASURED_TEMPERATURES, RECIPE_VALUES } from './constants';
import params from '../parameters.json';
import ProductTemplateTable from 'components/dashboard/product-template-table/ProductTemplateTable';

function VikingMaltResultsTable(props: any) {
    const headerFilter = [
        { label: 'Recipe Values', value: RECIPE_VALUES },
        { label: 'Measured Temperatures', value: MEASURED_TEMPERATURES },
        { label: 'Measured Moistures', value: MEASURED_MOISTURES },
    ];

    const [filterValues, setFilterValues] = useState([RECIPE_VALUES, MEASURED_TEMPERATURES, MEASURED_MOISTURES]);
    const [filterLabels, setFilterLabels] = useState([]);
    const [filterString, setFilterString] = useState('');
    const [datatablesDialogVisibility, setDatatablesDialogVisibility] = useState(false);

    const germination1 = props.results.freestyleJson.recommended_recipes_germination_1;
    const germination5 = props.results.freestyleJson.recommended_recipes_germination_5;
    const steeping1 = props.results.freestyleJson.recommended_recipes_steeping_1;
    const steeping5 = props.results.freestyleJson.recommended_recipes_steeping_5;

    const filteredGermination1 = germination1.filter((element: any) => element.referenceName !== 'used_batch_string');
    const filteredGermination5 = germination5.filter((element: any) => element.referenceName !== 'used_batch_string');
    const filteredSteeping1 = steeping1.filter((element: any) => element.referenceName !== 'used_batch_string');
    const filteredSteeping5 = steeping5.filter((element: any) => element.referenceName !== 'used_batch_string');

    const cleanedGerminationValues1 = filteredGermination1.map((element: any) => ({ ...element, value: Math.round(element.value * 100) / 100 }));
    const cleanedGerminationValues5 = filteredGermination5.map((element: any) => ({ ...element, value: Math.round(element.value * 100) / 100 }));

    const steepingCombined = filteredSteeping1.map((element: any, index: number) => {
        const combinedElement = structuredClone(element);
        combinedElement['recommended_recipes_steeping_1'] = element['value'];
        combinedElement['recommended_recipes_steeping_5'] = filteredSteeping5[index]['value'];
        delete combinedElement['value'];
        delete combinedElement['type'];
        return combinedElement;
    });

    for (let i = 0; i < steepingCombined.length; i++) {
        for (let j = 0; j < STEEPING_DISPLAY_NAMES.length; j++) {
            if (steepingCombined[i].referenceName === STEEPING_DISPLAY_NAMES[j].referenceName) {
                steepingCombined[i]['displayName'] = STEEPING_DISPLAY_NAMES[j]['displayName'];
            }
        }
    }

    const germinationCombined = cleanedGerminationValues1.map((element: any, index: number) => {
        const combinedElement = structuredClone(element);
        combinedElement['recommended_recipes_germination_1'] = element['value'];
        combinedElement['recommended_recipes_germination_5'] = cleanedGerminationValues5[index]['value'];
        delete combinedElement['value'];
        delete combinedElement['type'];
        return combinedElement;
    });

    for (let i = 0; i < germinationCombined.length; i++) {
        for (let j = 0; j < GERMINATION_DISPLAY_NAMES.length; j++) {
            if (germinationCombined[i].referenceName === GERMINATION_DISPLAY_NAMES[j].referenceName) {
                germinationCombined[i]['displayName'] = GERMINATION_DISPLAY_NAMES[j]['displayName'];
            }
        }
    }

    const steepingTransposed: any[] = [];
    for (let i = 0; i < steepingCombined.length; i++) {
        const displayName = steepingCombined[i]['displayName'];
        const top1 = steepingCombined[i].recommended_recipes_steeping_1;
        const top5 = steepingCombined[i].recommended_recipes_steeping_5;

        steepingTransposed[0] = { ...steepingTransposed[0], [displayName]: top1 };
        steepingTransposed[0] = { ...steepingTransposed[0], top: 'Top 1' };
        steepingTransposed[1] = { ...steepingTransposed[1], [displayName]: top5 };
        steepingTransposed[1] = { ...steepingTransposed[1], top: 'Top 5 (average)' };
    }

    const germinationTransposed: any[] = [];
    for (let i = 0; i < germinationCombined.length; i++) {
        const displayName = germinationCombined[i]['displayName'];
        const top1 = germinationCombined[i].recommended_recipes_germination_1;
        const top5 = germinationCombined[i].recommended_recipes_germination_5;

        germinationTransposed[0] = { ...germinationTransposed[0], [displayName]: top1 };
        germinationTransposed[0] = { ...germinationTransposed[0], top: 'Top 1' };
        germinationTransposed[1] = { ...germinationTransposed[1], [displayName]: top5 };
        germinationTransposed[1] = { ...germinationTransposed[1], top: 'Top 5 (average)' };
    }

    const [germinationTransposedFiltered, setGerminationTransposedFiltered] = useState(germinationTransposed);

    useEffect(() => {
        const tempLabels = document.getElementById('filter-multiselect').innerText.split(',');
        setFilterLabels(tempLabels);

        let flattenedFilterValues = [];
        if (filterValues.length > 0) {
            flattenedFilterValues = filterValues.reduce((a, b) => {
                return a.concat(b);
            });
        }

        const filtered = germinationTransposed.map((obj) => {
            const objKeys = Object.keys(obj);
            const tempObj = { ...obj };
            for (let i = 0; i < objKeys.length; i++) {
                const tempValue = flattenedFilterValues.filter((filterObj) => filterObj.displayName === objKeys[i]);
                if (tempValue.length === 0 && objKeys[i] !== 'dq total' && objKeys[i] !== 'CO2 total' && objKeys[i] !== 'top') {
                    delete tempObj[objKeys[i]];
                }
            }
            return tempObj;
        });
        setGerminationTransposedFiltered(filtered);
    }, [filterValues]);

    const bestBatches = props.results.freestyleJson.best_batches;

    const flattenedBestBatches = bestBatches.map((batch: any) => {
        const flatBatch = { ...batch, ...batch.selected_parameter_values };
        delete flatBatch['selected_parameter_values'];
        return flatBatch;
    });

    flattenedBestBatches.map((batch) => {
        for (const [key, value] of Object.entries(batch)) {
            params.map((param) => {
                if (key === param.id) {
                    batch[key] = value + param.unit;
                }
            });
        }
        return batch;
    });

    steepingTransposed.map(obj => {
        for (const [key, value] of Object.entries(obj)) {
                if (key !== "top") {
                    obj[key] = value + " h";
                }
        }
        return obj;
    })

    const productTemplateTablesNames = ['batch_parameters', 'steeping_parameters', 'germination_parameters', 'kilning_parameters'];

    const exploreClickHandler = () => {
        setDatatablesDialogVisibility(true);
        let tempString = '';
        flattenedBestBatches.map((batch) => {
            tempString += `(batch_nr:EQ:${batch.batch_nr};year:EQ:${batch.batch_year})|`;
        });
        setFilterString(tempString.slice(0, -1));
    };

    const bestBatchesHeader = (
        <div className='flex align-items-center gap-5'>
            <Tooltip target='.batches-info' />
            <p className='mb-0'>
                Best Batches <i className='pi pi-info-circle batches-info' data-pr-tooltip='Batches are sorted by best match. Best is at the top.'></i>
            </p>
            <Button label='Explore' className='p-button-outlined p-button-secondary' onClick={exploreClickHandler} />
            <Dialog visible={datatablesDialogVisibility} style={{ width: 'min(100vw, 1400px)', height: '800x' }} onHide={() => setDatatablesDialogVisibility(false)} maximizable>
                {productTemplateTablesNames.map((table) => {
                    return <ProductTemplateTable safeName={table} filter={filterString} allowAddEntry={false} allowRowEdit={false} />;
                })}
            </Dialog>
        </div>
    );

    const germinationHeader = (
        <div className='flex align-items-center' style={{ columnGap: '40rem' }}>
            <p className='mb-0'>Germination Recommendations</p>
            <MultiSelect
                id='filter-multiselect'
                options={headerFilter}
                value={filterValues}
                onChange={(e) => setFilterValues(e.value)}
                style={{ maxWidth: '200px' }}
                placeholder='Select Columns'
            />
        </div>
    );

    let headerGroup = (
        <ColumnGroup>
            <Row>
                <Column header='' rowSpan={3} style={{ borderTop: '1px solid #dee2e6', borderRight: '1px solid #dee2e6' }} />
                <Column header={germinationHeader} colSpan={17} style={{ borderTop: '1px solid #dee2e6' }} alignHeader='right' />
            </Row>
            <Row>
                <Column header='Recipe Values' colSpan={5} style={{ borderRight: '1px solid #dee2e6' }} alignHeader='center' hidden={!filterLabels.includes('Recipe Values')} />
                <Column header='' colSpan={2} style={{ borderRight: '1px solid #dee2e6' }} />
                <Column
                    header='Measured Temperatures'
                    colSpan={6}
                    style={{ borderRight: '1px solid #dee2e6' }}
                    alignHeader='center'
                    hidden={!filterLabels.includes('Measured Temperatures')}
                />
                <Column header='Measured Moistures' colSpan={4} alignHeader='center' hidden={!filterLabels.includes('Measured Moistures')} />
            </Row>
            <Row>
                {Object.keys(germinationTransposedFiltered[0]).map((key) => {
                    if (key !== 'top') {
                        return <Column header={key} key={key} alignHeader='center' />;
                    }
                })}
            </Row>
        </ColumnGroup>
    );

    return (
        <div className='flex flex-wrap gap-5'>
            <DataTable value={flattenedBestBatches} header={bestBatchesHeader}>
                <Column field='batch_year' alignHeader='center' bodyClassName='text-center' header='Year' />
                <Column field='batch_nr' alignHeader='center' bodyClassName='text-center' header='Number' />
                <Column field='variety' alignHeader='center' bodyClassName='text-center' header='Variety' />
                {params.map((param) => {
                    if (flattenedBestBatches[0][param.id] !== undefined) {
                        return <Column field={param.id} key={param.id} header={param.displayName} alignHeader='center' bodyClassName='text-center' />;
                    }
                })}
            </DataTable>
            <DataTable value={steepingTransposed} header='Steeping Recommendations' tableStyle={{ width: '400px' }}>
                <Column field='top' alignHeader='center' header='' style={{ width: '20%', textAlign: 'center' }} />
                <Column field='1st wet' alignHeader='center' header='1st wet' style={{ width: '20%', textAlign: 'center' }} />
                <Column field='1st dry' alignHeader='center' header='1st dry' style={{ width: '20%', textAlign: 'center' }} />
                <Column field='2nd wet' alignHeader='center' header='2nd wet' style={{ width: '20%', textAlign: 'center' }} />
                <Column field='2nd dry' alignHeader='center' header='2nd dry' style={{ width: '20%', textAlign: 'center' }} />
            </DataTable>
            <DataTable value={germinationTransposedFiltered} headerColumnGroup={headerGroup} className='w-full'>
                <Column field='top' />
                {GERMINATION_DISPLAY_NAMES.map((param) => {
                    if (germinationTransposedFiltered[0][param.displayName] !== undefined) {
                        return <Column field={param.displayName} key={param.referenceName} header={param.displayName} alignHeader='center' bodyClassName='text-center' />;
                    }
                })}
            </DataTable>
        </div>
    );
}

export default VikingMaltResultsTable;
